import { useCallback, useRef } from 'react';
import { useRouter } from 'expo-router';
import {
  useAuthStore,
  useAgentStore,
  useChatInputStore,
  useCoworkerStore,
  useModalStore,
} from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import reportWorkLaunchError from '@/lib/reportWorkLaunchError';
import { navigateToCreatedWork } from '@/lib/startWorkFromPrompt';
import { blockIfWorkModeDisabled } from '@/lib/workModeAvailability';

export default function useStartAgentWorkChat() {
  const router = useRouter();
  const requireAuth = useRequireAuth();
  const { openModal } = useModalStore((state) => state.actions);
  const isPlus = useAuthStore((state) => state.user?.flags?.isPlus);
  const startingRef = useRef(false);

  const startAgentWorkChat = useCallback((agent, { source = 'start_agent_work_chat', prompt, fileIds = [], requestTask = false, requireWork = false } = {}) => {
    if (!agent?.id || startingRef.current) return;

    return requireAuth(async () => {
      // Open the job form; apply the Work gate when the user submits it.
      if (requestTask) {
        openModal('GiveBotJobModal', { agent, source });
        return true;
      }

      if (!isPlus) {
        openModal('PricingModal', {
          title: 'Put your bot to work',
          subtitle: 'Upgrade to Plus to start Work chats with your bots.',
        });
        return false;
      }
      if (blockIfWorkModeDisabled()) return false;

      const text = String(prompt || '').trim();
      if (requireWork && !text) return false;

      // Empty composer with this agent selected — do not send a first message.
      if (!text) {
        const composer = useChatInputStore.getState();
        useAgentStore.getState().addAgents([agent]);
        composer.setContext('new');
        composer.clearInput(true);
        composer.setSelectedAgents([agent.id], { botId: agent.id });
        useCoworkerStore.getState().setComposerMode('coworker');
        router.push('/app');
        return true;
      }

      startingRef.current = true;

      try {
        const coworkerState = useCoworkerStore.getState();
        const created = await coworkerState.createTask({
          prompt: text,
          agentId: agent.id,
          fileIds: Array.isArray(fileIds) ? fileIds : [],
          ...coworkerState.getComposerLaunchOptions(),
        });
        navigateToCreatedWork(router, created);
        return true;
      } catch (error) {
        reportWorkLaunchError(error, { source, fallback: 'Failed to start' });
        return false;
      } finally {
        startingRef.current = false;
      }
    }, {
      source,
      title: `Sign in to work with ${agent?.name || 'this bot'}`,
      subtitle: 'Create a free account so we can save the conversation and any files you share.',
      icon: 'robot',
    });
  }, [
    isPlus,
    openModal,
    requireAuth,
    router,
  ]);

  return startAgentWorkChat;
}
